import { BaseTranslations } from '../../../baseTranslations';
import { ContactMe } from '../../../types/car-insurance';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext/useCmsClientContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { FeLink } from '../../FeLink';
import { MaterialIcon } from '../../MaterialIcon';
import { WhatsAppButton } from './WhatsappButton';

interface SelectContactMethodProps {
  contactMethod: string;
  createWALink: () => string;
  stepNumber: number;
  contactInfo: ContactMe;
  pushDataLayer: PushDataLayerFn;
}

export const SelectContactMethod: React.FC<SelectContactMethodProps> = ({
  contactMethod,
  createWALink,
  stepNumber,
  contactInfo,
  pushDataLayer,
}) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const contactPhone = contactInfo.phone.number;
  const contactEmail = contactInfo.email.email;

  const trackSnackbarCallNowButton = () => {
    pushDataLayer({
      event: 'navigate',
      component_type: 'overlay',
      component_title: `Lead Collection Form_Step${stepNumber}_Call us now`,
      action: 'click',
    });
  };

  const trackSnackbarEmailNowButton = () => {
    pushDataLayer({
      event: 'navigate',
      component_type: 'overlay',
      component_title: `Lead Collection Form_Step${stepNumber}_Email us now`,
      action: 'click',
    });
  };

  const trackWhatsappCTAButton = () => {
    pushDataLayer({
      event: 'navigate',
      component_type: 'cta-button',
      component_title: `Lead Collection Form_Step${stepNumber}_WhatsApp us now`,
      action: 'click',
      index: '',
    });
  };

  function selectButton(contactMethod: string) {
    switch (contactMethod) {
      case 'Email':
        return {
          content: (
            <div
              role="button"
              tabIndex={0}
              className="flex items-center justify-center gap-1 outline-none"
              onClick={trackSnackbarEmailNowButton}
            >
              <p className="font-semibold text-white text-base">
                {staticText.email_now}
              </p>
              <MaterialIcon
                icon="mail"
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="text-white text-[22px]"
              />
            </div>
          ),
          href: `mailto:${contactEmail}`,
          className:
            'w-full flex items-center justify-center h-9 bg-[#5D8433] text-white rounded-lg',
        };

      case 'WA':
        return {
          content: (
            <WhatsAppButton
              text={staticText.WA}
              onClick={() => window.open(createWALink(), '_blank')}
              trackClick={trackWhatsappCTAButton}
              className="h-9 w-full"
              type="button"
              iconSize={22}
            />
          ),
          href: createWALink(),
        };

      default:
        return {
          content: (
            <div
              role="button"
              tabIndex={0}
              className="flex items-center justify-center gap-1 outline-none"
              onClick={trackSnackbarCallNowButton}
            >
              <MaterialIcon
                icon="call"
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="text-white text-[22px]"
              />
              <p className="font-semibold text-white text-base">
                {staticText.phone_now}
              </p>
            </div>
          ),
          href: `tel:${contactPhone}`,
          className:
            'w-full flex items-center justify-center h-9 bg-[#5D8433] text-white rounded-lg',
        };
    }
  }

  const buttonConfig = selectButton(contactMethod);

  return (
    <div className="mt-2 flex w-full flex-col items-center justify-center gap-1 lg:gap-2">
      {contactMethod === 'WA' ? (
        buttonConfig.content
      ) : (
        <FeLink href={buttonConfig.href} className={buttonConfig.className}>
          {buttonConfig.content}
        </FeLink>
      )}
      <p className="text-center font-medium text-gray-700 text-xs md:text-sm lg:text-base">
        {staticText.timing}
      </p>
    </div>
  );
};
